import React from 'react';
import PropTypes from 'prop-types';
import InteractionOutcomeIndicator from '../../interactionOutcome/InteractionOutcomeIndicator';
import constants from '../../../../qti/Constants';
import validDragEvents from './validDragEvents';

import styles from './gapMatchInteraction.scss';
import ImageDisplay from '../../media/image/ImageDisplay';
import interactionOutcomeUtils from '../../../../qti/utils/InteractionOutcomeUtils';
import LoadTracker from '../../../../services/LoadTracker';
import customFieldsService from '../../../../services/CustomFieldsService';
import sessionReader from '../../../../store/readHelpers/SessionReader';
import MathDisplay from '../../mathDisplay/MathDisplay';
import settingsReader from '../../../../store/readHelpers/SettingsReader';
import questionReader from '../../../../store/readHelpers/QuestionReader';
import marketplacePropertiesService from '../../../../services/MarketplacePropertiesService';
import GapMatchInteractionServices from '../../../../services/questionView/GapMatchInteractionServices';
import customFieldsConstants from '../../../../constants/CustomFieldsConstants';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from '../../../../services/a11yIIBScreenReaderService';
import { t } from '../../../../services/i18nService';
import gapMatchInteractionSelector from '../../../../selectors/question/interactions/gapMatchInteraction/GapMatchInteractionSelector';

const keys = constants.keyCodes;
const toBeFocusedOnNextMounting = {};

class GapChoice extends React.Component {
    static propTypes = {
        correctResponses: PropTypes.array,
        gapMap: PropTypes.object.isRequired,
        gapHeight: PropTypes.number,
        gapWidth: PropTypes.number,
        gapChoiceModel: PropTypes.object.isRequired,
        gapModel: PropTypes.object,
        handleUserResponse: PropTypes.func.isRequired,
        isReadOnly: PropTypes.bool,
        hidden: PropTypes.bool,
        model: PropTypes.object.isRequired,
        questionModel: PropTypes.object.isRequired,
        gapTextToGapMap: PropTypes.object.isRequired,
        gapToGapTextMap: PropTypes.object.isRequired,
        reportReady: PropTypes.func,
        services: PropTypes.object.isRequired,
        shouldAutoSizeTokens: PropTypes.bool,
        extendedRenderingActivated: PropTypes.bool,
        userResponse: PropTypes.array,
        index: PropTypes.number,
    };

    static defaultProps = {
        reportReady: () => undefined,
    };

    static showImage(gapChoiceModel) {
        const { mediaModel } = gapChoiceModel;
        return mediaModel && mediaModel.type && mediaModel.type.includes('image');
    }

    static showText(gapChoiceModel) {
        return gapChoiceModel.textContent.length > 0;
    }

    static showOutcomeIndicator(props) {
        return props.correctResponses !== undefined;
    }

    static shouldFocusSelectedGapChoice(gapIdentifier, gapModels) {
        return !(!gapModels.length || gapIdentifier);
    }

    constructor(props) {
        const { gapChoiceModel, reportReady } = props;
        const { identifier } = gapChoiceModel;
        super(props);

        this.state = {
            focused: false,
        };

        this.readyTracker = new LoadTracker(reportReady, `DraggableNode ${identifier}`);

        this.reportMounted = this.readyTracker.addItemToTrack(`DraggableNode ${identifier} > mounted`);
        if (GapChoice.showImage(gapChoiceModel)) {
            this.reportImageReady = this.readyTracker.addItemToTrack(`DraggableNode ${identifier} > image`);
        }
        if (GapChoice.showText(gapChoiceModel)) {
            this.reportTextReady = this.readyTracker.addItemToTrack(`DraggableNode ${identifier} > text`);
        }
        if (GapChoice.showOutcomeIndicator(props)) {
            this.reportIconReady = this.readyTracker.addItemToTrack(`DraggableNode ${identifier} > outcomeIcon`);
        }
        this.readyTracker.setAllItemsAdded();

        this.handleTextElementRef = this.handleTextElementRef.bind(this);

        this.handleDragEnd = this.handleDragEnd.bind(this, props);

        this.ANNOUNCE_PARAGRAPH_DELAY = 1600;
    }

    componentDidMount() {
        // The following prevents scrolling on drag.
        this.scrollable = true;
        this.listener = (e) => {
            if (!this.scrollable) {
                e.preventDefault();
            }
        };
        document.addEventListener('touchmove', this.listener, { passive: false });
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleKeyUp = this.handleKeyUp.bind(this);

        window.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('keyup', this.handleKeyUp);
        if (toBeFocusedOnNextMounting[this.props.gapChoiceModel.identifier]) {
            this.rootElement.focus();
            delete toBeFocusedOnNextMounting[this.props.gapChoiceModel.identifier];
        }
        this.reportMounted(true);
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('keyup', this.handleKeyUp);
        if (this.paragraphAnnouncementTimeout) {
            clearTimeout(this.paragraphAnnouncementTimeout);
        }
    }

    handleDrag = () => {
        this.scrollable = false;
    };

    handleDragStart = (e) => {
        const { model } = this.props;
        const dragData = {
            gapTextIdentifier: this.props.gapChoiceModel.identifier,
            questionId: this.props.questionModel.contentRevisionId,
            modelIdentifier: model.identifier,
        };
        const target = this.rootElement;
        if (target.parentElement.dataset.identifier) {
            dragData.gapIdentifier = target.parentElement.dataset.identifier;
        }
        e.dataTransfer.setData('text', JSON.stringify(dragData));
        target.classList.add('dragging');
        const dropZones = document.getElementsByClassName('gap');
        Array.from(dropZones).forEach((dropZone) => {
            dropZone.classList.add('dragging');
        });
        if (!model.reuseAlternatives) {
            target.classList.add('opacity');
        }
        validDragEvents.push(e);
    };

    /**
     *
     * @param props
     * @param {Event} e
     */
    handleDragEnd = (props, e) => {
        const elementFromPoint = document.elementFromPoint(e.clientX, e.clientY);
        const elementFromPointId = elementFromPoint.closest('[id^="interactionWrapper"]');
        let allowDropAction = true;
        if (elementFromPointId) {
            const contentRID = elementFromPointId.id.split('interactionWrapper_')[1];
            const { questionModel } = props;
            allowDropAction = parseInt(contentRID, 10) === questionModel.contentRevisionId;
        }

        if (
            elementFromPoint !== this.rootElement &&
            elementFromPoint !== this.rootElement.parentElement &&
            allowDropAction
        ) {
            this.removeTokenFromGap();
        }
        this.rootElement.classList.remove('dragging', 'opacity');
        const gaps = document.getElementsByClassName('gap');
        Array
            .from(gaps)
            .forEach((dropZone) => {
                dropZone.classList.remove('dragging');
            });
        this.scrollable = true;
        validDragEvents.splice(
            validDragEvents.indexOf(e),
            1
        );
    };

    removeTokenFromGap = () => {
        const target = this.rootElement;
        const gapTextIdentifier = target.dataset.identifier;
        const prevGapIdentifier = target.parentElement.dataset.identifier;
        this.props.handleUserResponse(gapTextIdentifier, undefined, prevGapIdentifier);
    };

    handleArrowKeyUp = (e) => {
        const direction = e.keyCode < keys.RIGHT ? 'up' : 'down';
        const gapIdentifier = this.getNextAvailableGapIdentifier(direction);
        const prevGapIdentifier = e.target.parentElement.dataset.identifier;
        const isA11ySupportedForV4AndAbove = GapMatchInteractionServices.isA11ySupportedForV4AndAbove();

        toBeFocusedOnNextMounting[this.props.gapChoiceModel.identifier] = true;
        this.props.handleUserResponse(this.props.gapChoiceModel.identifier, gapIdentifier, prevGapIdentifier);

        const elementShouldBeFocused = GapChoice.shouldFocusSelectedGapChoice(gapIdentifier, this.props.model.gapModels);
        if (elementShouldBeFocused) {
            const questionResponseIdentifier = this.props.model.target;
            const elementToFocus = document.getElementById(questionResponseIdentifier)
                .querySelector(`[data-identifier=${this.props.gapChoiceModel.identifier}]`);
            if (elementToFocus) {
                elementToFocus.focus();
            }
        }

        // Announce movement to screen readers after DOM updates
        if (isA11ySupportedForV4AndAbove && gapIdentifier) {
            window.setTimeout(() => {
                this.announceKeyboardMove(gapIdentifier);
            }, 0);
        }

        return false;
    };

    announceKeyboardMove = (targetGapIdentifier) => {
        try {
            // Find target gap and its paragraph
            const targetGapEl = document.querySelector(`.gap[data-identifier="${targetGapIdentifier}"]`);
            const closestPTag = targetGapEl ? targetGapEl.closest('p') : undefined;
            if (!closestPTag) return;

            const movedTokenIdentifier = this.props.gapChoiceModel.identifier;
            const tokenDataEl = document.querySelector(`[data-identifier="${movedTokenIdentifier}"]`);
            const tokenData = tokenDataEl ? (tokenDataEl.textContent || '').trim() : '';

            // Update the aria-live region rendered by the corresponding Gap component
            const liveRegion = targetGapEl && targetGapEl.previousElementSibling &&
                targetGapEl.previousElementSibling.getAttribute('aria-live') === 'assertive'
                ? targetGapEl.previousElementSibling
                : undefined;

            if (liveRegion) {
                // First, announce movement to screen readers immediately
                const tokenGapMessage = gapMatchInteractionSelector.getTokenGapAnnouncement(tokenData, targetGapIdentifier);
                liveRegion.textContent = tokenGapMessage;

                // After a short delay, announce the full paragraph
                if (this.paragraphAnnouncementTimeout) {
                    window.clearTimeout(this.paragraphAnnouncementTimeout);
                }

                this.paragraphAnnouncementTimeout = window.setTimeout(() => {
                    // Re-calculate the paragraph text to ensure we have the latest state
                    const currentClosestPTag = targetGapEl.closest('p');
                    if (!currentClosestPTag) return;

                    // Build full paragraph with current visible values in gaps
                    const liveMessageRaw = gapMatchInteractionSelector.getParagraphText(currentClosestPTag);
                    const liveMessage = gapMatchInteractionSelector.getFullParagraphAnnouncement(liveMessageRaw);


                    liveRegion.textContent = liveMessage;
                }, this.ANNOUNCE_PARAGRAPH_DELAY);
            }
        } catch (err) {
            // Fail silently; announcing is best-effort and should not break interaction
        }
    };

    handleKeyUp = (e) => {
        const { focused } = this.state;
        if (focused && e.keyCode >= keys.LEFT && e.keyCode <= keys.DOWN) {
            e.preventDefault();
            return this.handleArrowKeyUp(e);
        } else if (focused && (e.keyCode === keys.DELETE || e.keyCode === keys.BACKSPACE)) {
            this.removeTokenFromGap();
        }
        return true;
    };

    handleKeyDown = (e) => {
        const { focused } = this.state;
        if (focused && e.keyCode >= keys.LEFT && e.keyCode <= keys.DOWN) {
            e.preventDefault();
            return false;
        }

        return true;
    };

    handleOnFocus = () => {
        this.setState({ focused: true });
    };

    async handleTextElementRef(element) {
        if (element === null) {
            return;
        }
        await MathDisplay.showMath(element);
        this.reportTextReady(true);
    }

    /**
     * @param {String} direction 'up' or 'down'
     */
    getNextAvailableGapIdentifier = (direction) => {
        const { gapMap, gapChoiceModel, model, gapToGapTextMap, gapTextToGapMap, userResponse } = this.props;

        if (userResponse.length === model.gapModels.length) {
            return undefined; // All gaps are filled.
        }

        const goingDown = direction === 'down';
        const matchedWith = gapTextToGapMap[gapChoiceModel.identifier];
        let myPosition = model.gapModels.indexOf(gapMap[matchedWith]);
        if (!goingDown && myPosition === -1) {
            myPosition = model.gapModels.length - 1;
        }

        if (goingDown) {
            for (let i = myPosition + 1; i < model.gapModels.length; i++) {
                if (!gapToGapTextMap[model.gapModels[i].identifier]) {
                    return model.gapModels[i].identifier;
                }
            }
        } else {
            for (let i = myPosition; i >= 0; i--) {
                if (!gapToGapTextMap[model.gapModels[i].identifier]) {
                    return model.gapModels[i].identifier;
                }
            }
        }

        return undefined;
    };

    getAnswerOption = () => {
        const { gapChoiceModel, gapModel } = this.props;
        if (gapModel) {
            return `${gapChoiceModel.identifier} ${gapModel.identifier}`;
        }
        return '';
    };

    getIsAttempted = () => {
        const { gapChoiceModel, userResponse } = this.props;
        let isAttempted = false;

        for (let i = 0; i < userResponse.length; i++) {
            const idx = userResponse[i].indexOf(gapChoiceModel.identifier);
            if (idx > -1) {
                isAttempted = true;
                break;
            }
        }

        return isAttempted;
    };

    setAsUnFocused = () => {
        this.setState({ focused: false });
    };

    getPBNumbering = (index) => {
        const { questionModel, services } = this.props;
        if (customFieldsService.isMatchingHeadings(questionModel)) {
            return services.utilService.toRomanNumeral(index);
        }
        return services.utilService.toLatinLetter(index);
    };

    addPrintStyles = (style) => {
        if (!style) return;
        const printStyle = sessionReader.getPrintParam('printStyle');
        const isPageBreakingPrintView = settingsReader.shouldPageBreakOnPrintView();
        if (printStyle && !isPageBreakingPrintView) {
            style.width = 'auto';
            style.maxWidth = 'none';
            style.whiteSpace = 'normal';
            style.wordBreak = 'normal';
            style.overflowWrap = 'break-word';
            style.fontFamily = 'Arial, sans-serif';
            style.textRendering = 'geometricPrecision';
            style.WebkitFontSmoothing = 'subpixel-antialiased';
        }
    };

    addMultiInteractionGapStyles = (style) => {
        if (sessionReader.isPrintStylePaperBased()) return;
        const { model, gapChoiceModel, questionModel } = this.props;
        const isMultipleInteractionsGapMatchQuestion = questionReader.isGapMatchQuestionWithMultipleInteractions(this.props.questionModel);
        const shouldAutoSizeTokens = model.shouldAutoSizeTokens;
        const { imageHeight, imageWidth } = GapMatchInteractionServices.getImageTokenDimensions(gapChoiceModel);
        const maximumWidth = (0.3 * window.innerWidth) + (shouldAutoSizeTokens ? 0 : 3);
        const isPBRenderOptionAndTokenRight = settingsReader.isTokenPositionRight(questionModel, model?.tokenPosition,
            customFieldsConstants.paperBasedRenderOptions.IGM_PIN_RIGHT);
        if (isMultipleInteractionsGapMatchQuestion && style && !imageHeight && !imageWidth && !isPBRenderOptionAndTokenRight) {
            const widthKey = shouldAutoSizeTokens ? 'width' : 'maxWidth';
            style[widthKey] = `${maximumWidth}px`;
        }
        if (isMultipleInteractionsGapMatchQuestion && style && imageHeight && imageWidth) {
            style.width = shouldAutoSizeTokens ? `${maximumWidth}px` : `${imageWidth}px`;
            style.height = `${imageHeight}px`;
        }
    };

    applyPaperBasedEnhancementTokenStyle(classes, questionModel) {
        const hasIGMMetadata = settingsReader.isPaperBasedIGMSelected(questionModel);

        if (hasIGMMetadata) {
            classes.push(styles.paperBasedEnhancementToken);
        }
    }


    render() {
        const {
            gapChoiceModel,
            gapHeight,
            gapWidth,
            hidden,
            isReadOnly,
            correctResponses,
            services,
            shouldAutoSizeTokens,
            questionModel,
            index,
            extendedRenderingActivated,
        } = this.props;
        const isMultipleInteractionsGapMatchQuestion = questionReader.isGapMatchQuestionWithMultipleInteractions(questionModel);
        const answerOption = this.getAnswerOption();
        const isAttempted = this.getIsAttempted();
        const tabIndex = isReadOnly ? undefined : '0';
        const draggable = !isReadOnly && !sessionReader.isPrintStylePaperBased();
        const onFocus = isReadOnly ? undefined : this.handleOnFocus;
        const style = {
            visibility: hidden ? 'hidden' : 'visible',
        };
        const isA11ySupportedForV4AndAbove =
        a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v4);

        const a11yAttributes = isA11ySupportedForV4AndAbove ? {
            'aria-roledescription': t('ariaRoleDescription.draggableItem'),
            'role': 'button',
        } : {};

        if (shouldAutoSizeTokens && !extendedRenderingActivated && !isMultipleInteractionsGapMatchQuestion) {
            if (!marketplacePropertiesService.isEnableTwoInlineGapMatchAreas()) style.height = `${gapHeight + 2}px`; // +2 matches the margin of -1px
            style.width = `${gapWidth + 3}px`; // +2 matches the margin of -1px and 1px extra to round up browser clientWidth
        }

        let responseClassName = '';
        if (correctResponses) {
            const isCorrect = interactionOutcomeUtils.isAnswerCorrect(answerOption, correctResponses);
            responseClassName = isCorrect ? 'correct' : 'incorrect';
        }

        const classes = [styles.gapChoice, 'draggable', 'gapText', responseClassName];
        const isMatchHeadings = customFieldsService.isMatchingHeadings(questionModel);

        if (isMatchHeadings) {
            classes.push(styles.matchHeadings);
        }

        if (sessionReader.isPrintStylePaperBased()) {
            classes.push(styles.paperBasedToken);
        }

        if (extendedRenderingActivated) {
            classes.push(styles.extendedTokens);
            style.maxHeight = `${gapHeight + 500}px`;
            this.addPrintStyles(style);
        }
        this.addMultiInteractionGapStyles(style);
        const multitextNodeClass = marketplacePropertiesService.isEnableTwoInlineGapMatchAreas()
            ? 'multi-interaction-text-node' : '';

        this.applyPaperBasedEnhancementTokenStyle(classes, questionModel);

        return (
            <div
                data-identifier={gapChoiceModel.identifier}
                className={classes.join(' ')}
                onFocus={onFocus}
                onBlur={this.setAsUnFocused}
                ref={(element) => { this.rootElement = element; }}
                tabIndex={tabIndex}
                onDragStart={this.handleDragStart}
                onDrag={this.handleDrag}
                onDragEnd={this.handleDragEnd}
                draggable={draggable}
                style={style}
                {...a11yAttributes}
            >
                { sessionReader.isPrintStylePaperBased() &&
                    <span className="numbering">{this.getPBNumbering(index + 1)}</span>
                }

                { GapChoice.showImage(gapChoiceModel) &&
                    <ImageDisplay
                        model={gapChoiceModel.mediaModel}
                        reportReady={this.reportImageReady}
                        useModelWidthAndHeight
                    />
                }

                { GapChoice.showText(gapChoiceModel) &&
                    <div
                        className={[multitextNodeClass, styles.textNode].join(' ')}
                        dangerouslySetInnerHTML={{ __html: gapChoiceModel.textContent }}
                        ref={this.handleTextElementRef}
                    />
                }

                { GapChoice.showOutcomeIndicator(this.props) &&
                    <InteractionOutcomeIndicator
                        answerOption={answerOption}
                        isAttempted={isAttempted}
                        correctResponses={correctResponses}
                        translationService={services.translationService}
                        onReady={this.reportIconReady}
                    />
                }
            </div>
        );
    }
}

export default GapChoice;
